// @flow
import React, {useState} from 'react';
import {makeStyles, useTheme} from '@material-ui/core/styles';
import {useMediaQuery, MenuItem, Typography, Box} from '@material-ui/core';
import {Pagination} from '@material-ui/lab';
import {useCustomerComplaints} from '../../../customer-complaints/useCustomerComplaints';
import {getCustomerComplaintService} from '../../../customer-complaints/promises';
import ComplaintsHeader from './ComplaintsHeader/ComplaintsHeader';
import ComplaintsTable from './ComplaintsTable/ComplaintsTable';
import ComplaintsMobileCards from './ComplaintsMobileCards/ComplaintsMobileCards';
import ComplaintDetailModal from './ComplaintDetailModal/ComplaintDetailModal';
import LoadingSpinner from '../../../../elements/LoadingSpinner/LoadingSpinner';
import type {
  CustomerComplaint,
  CustomerComplaintState,
} from '../../../../entities/customer-complaints';
import CustomSelect from '../../../../elements/CustomSelect/CustomSelect';

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2, 3),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2),
    },
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    backgroundColor: 'white',
    borderRadius: '12px',
    overflow: 'hidden',
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '1rem',
    padding: '1rem 0',
  },

  sizeSelect: {
    minWidth: 120,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(6, 2),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  emptyStateIcon: {
    fontSize: '4rem',
    marginBottom: theme.spacing(2),
    opacity: 0.5,
  },
  emptyStateTitle: {
    marginBottom: theme.spacing(1),
    fontWeight: 600,
  },
  emptyStateSubtitle: {
    opacity: 0.7,
  },
}));

export default function StoreComplaints() {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [
    selectedComplaint,
    setSelectedComplaint,
  ] = useState<CustomerComplaint | null>(null);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [peerPagePagination, setPeerPagePagination] = useState<number>(10);

  const {
    complaints,
    allComplaints,
    loading,
    filter,
    pagination,
    setFilter,
    updateComplaintStatus,
    exportComplaints,
    isComplaintOverdue,
    changePage,
    changePageSize,
  } = useCustomerComplaints();

  const handleViewDetail = async (complaint: CustomerComplaint) => {
    try {
      const detailedComplaint = await getCustomerComplaintService({
        customerComplaintId: complaint.id,
      });
      setSelectedComplaint(detailedComplaint);
      setModalOpen(true);
    } catch (error) {
      console.error('Error fetching complaint details:', error);
      setSelectedComplaint(complaint);
      setModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedComplaint(null);
  };

  const handleStatusUpdate = async (
    complaintId: string,
    newStatus: CustomerComplaintState
  ) => {
    try {
      await updateComplaintStatus(complaintId, newStatus);

      if (selectedComplaint && selectedComplaint.id === complaintId) {
        setSelectedComplaint({
          ...selectedComplaint,
          state: newStatus,
        });
      }
    } catch (error) {
      console.error('Error updating complaint status:', error);
    }
  };

  if (loading) {
    return (
      <div className={classes.root}>
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <ComplaintsHeader
        filter={filter}
        onFilterChange={setFilter}
        onExport={exportComplaints}
        totalComplaints={allComplaints.length}
      />

      {complaints.length === 0 && !loading ? (
        <Box className={classes.emptyState}>
          <Typography variant="h2" className={classes.emptyStateIcon}>
            <span role="img" aria-label="Documento">
              📝
            </span>
          </Typography>
          <Typography variant="h6" className={classes.emptyStateTitle}>
            No hay quejas registradas
          </Typography>
          <Typography variant="body2" className={classes.emptyStateSubtitle}>
            Cuando se registren quejas de clientes, aparecerán aquí para su
            gestión.
          </Typography>
        </Box>
      ) : (
        <>
          {/* Desktop Table */}
          <ComplaintsTable
            complaints={complaints}
            pagination={pagination}
            loading={loading}
            onUpdateStatus={handleStatusUpdate}
            onViewDetail={handleViewDetail}
            onChangePage={changePage}
            onChangePageSize={changePageSize}
            isComplaintOverdue={isComplaintOverdue}
          />

          {/* Mobile Cards */}
          {isMobile && (
            <ComplaintsMobileCards
              complaints={complaints}
              onViewDetail={handleViewDetail}
              onUpdateStatus={handleStatusUpdate}
              isComplaintOverdue={isComplaintOverdue}
            />
          )}

          {pagination && (
            <div className={classes.pagination}>
              <Pagination
                count={
                  pagination &&
                  (pagination.size >= pagination.total
                    ? 1
                    : Math.ceil(pagination.total / pagination.size))
                }
                page={pagination.page + 1}
                onChange={(e, page) => {
                  changePage(page - 1);
                }}
                color="primary"
              />
              <CustomSelect
                className={classes.sizeSelect}
                value={peerPagePagination}
                onChange={e => {
                  const newSize = parseInt(e.target.value, 10);
                  setPeerPagePagination(newSize);
                  changePageSize(newSize);
                }}
              >
                <MenuItem value={5}>5 por página</MenuItem>
                <MenuItem value={10}>10 por página</MenuItem>
                <MenuItem value={25}>25 por página</MenuItem>
                <MenuItem value={50}>50 por página</MenuItem>
              </CustomSelect>
            </div>
          )}
        </>
      )}

      {/* Detail Modal */}
      <ComplaintDetailModal
        open={modalOpen}
        complaint={selectedComplaint || undefined}
        onClose={handleCloseModal}
        onUpdateStatus={handleStatusUpdate}
        isComplaintOverdue={isComplaintOverdue}
      />
    </div>
  );
}
